/****************************************************
 * Module Name:  assignTags.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import logo from '../../images/pansuriya.jpg'
import "./../../style/modalstyle.css";
import CommonSnackbar from "../common/commonSnackBar";

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { LoadingButton } from "@mui/lab";
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

const AssignTag = ({ reassignTags, handleClose, show, handleUpdateTag, newTag, loading }) => {

    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [successSnackbar, setSuccessSnackbar] = useState(false);
    const [rows, setRows] = useState(reassignTags.sort((a, b) => a.stoneid - b.stoneid));

    // Create a connection
    const ws = useRef(null);

    useEffect(() => {
        startScanning();
    }, [])

    const startScanning = () => {

        // Open WebSocket connection and start scanning
        if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
            // WebSocket doesn't exist or was closed — create a new one
            ws.current = new WebSocket(Constants.RFID_API);
            ws.current.onopen = () => {
                ws.current.send("START_SCAN");
            };
        } else if (ws.current.readyState === WebSocket.CONNECTING) {
            // It's still connecting — safely assign onopen
            ws.current.onopen = () => {
                ws.current.send("START_SCAN");
            };
        } else if (ws.current.readyState === WebSocket.OPEN) {
            // Already open, just send
            ws.current.send("START_SCAN");
        }

        let newRows = rows;
        let lstTags = new Set();
        let tagLength = -1;
        ws.current.onmessage = (e) => {
            let message = typeof e.data === 'string' ? e.data : String(e.data);
            if (message.includes("TAG: ")) {
                const parts = message.split("TAG: ");
                if (parts.length === 2) {
                    const remaining = parts[1].trim(); // remove extra whitespace
                    remaining.split(',').forEach(str => lstTags.add(str.trim())); // split and trim each item
                }

                if (tagLength !== lstTags.size && lstTags.size <= newRows.length) {
                    const tagsArray = [...lstTags];
                    const idx = lstTags.size - 1;
                    newRows[idx].tag = tagsArray[idx];
                    newRows[idx].status = "TAGGED";

                    setRows([...newRows]);

                    if (lstTags.size > 7) {
                        scrollToElement();
                    }

                    tagLength = lstTags.size;
                }
            } else if (message.includes("SCAN_COMPLETED")) {
                if (lstTags.size < newRows.length) {
                    ws.current.send("START_SCAN");
                } else {
                    ws.current.close();
                    ws.current = null;
                }
            }
        }
    }

    // Function to scroll to an element with the given stoneid
    const scrollToElement = () => {
        const container = document.querySelector('.table-scroll-container');
        if (container) {
            container.scrollBy({ top: 42, behavior: 'smooth' });
        }
    };

    const handleData = () => {
        let data = [];
        rows.map(row => data.push({ stoneid: row.stoneid, tag: row.tag }));
        handleUpdateTag(newTag ? { stones: data } : data);

        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            ws.current.close();
            ws.current = null;
        }
    }

    const closeModal = () => {
        handleClose();
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            ws.current.close();
            ws.current = null;
        }
    }

    return (

        <div>

            {successSnackbar ? (<CommonSnackbar
                show={successSnackbar}
                message="Tags Assigned Successfully"
                variant={"success"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Success"}
                handleClose={() => setSuccessSnackbar(false)}
            />) : null}

            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />

            <Modal backdrop="static" show={show} onHide={closeModal} size="lg" enforceFocus={false}>

                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Assign Tags
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <TableContainer component={Paper} className="table-scroll-container" sx={{ width: "100%", height: "49vh", overflowY: "auto", }}>
                        <Table
                            stickyHeader
                            sx={{
                                minWidth: 650,
                                '& .MuiTableCell-root': {
                                    textAlign: 'center',
                                    height: '36px', // row height
                                    padding: '8px', // optional: tighten padding to match height
                                },
                            }}
                            aria-label="simple table"
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Stone ID</TableCell>
                                    <TableCell>Certificate ID</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="center">New Tag</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow
                                        key={row.stoneid}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: row.tag !== null ? "#fef0f4" : null }}
                                    >
                                        <TableCell component="th" scope="row" sx={{ width: '120px' }}>
                                            <div id={row.stoneid}>{row.stoneid}</div>
                                        </TableCell>
                                        <TableCell sx={{ width: '150px' }}>{row.certificateid}</TableCell>
                                        <TableCell sx={{ width: '150px' }} align="center">{row.status}</TableCell>
                                        <TableCell align="center">{row.tag === null ? " - " : row.tag}</TableCell>
                                        <TableCell align="center">
                                            <DeleteForeverIcon
                                                style={{ cursor: "pointer", fontSize: "1.5rem" }}
                                                color={"error"}
                                                onClick={() => setRows(prevRows => prevRows.filter((stone) => stone.stoneid !== row.stoneid))}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Modal.Body>

                <Modal.Footer>
                    <LoadingButton
                        variant="contained"
                        color="success"
                        style={{ marginRight: '20px' }}
                        onClick={handleData}
                        loading={loading}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                    >
                        Submit
                    </LoadingButton>
                    <Button variant="contained" color="error" onClick={closeModal} disabled={loading}>
                        Close
                    </Button>
                </Modal.Footer>

            </Modal>

        </div>
    );
};

export default AssignTag;
